"use client";

import * as React from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Send, Check, Mail, Phone, Clock, MapPin } from "lucide-react";
import { Button } from "@/components/ui/Button";

export const Footer: React.FC = () => {
  const pathname = usePathname();
  if (pathname === "/pitch" || pathname === "/brand/logo-preview" || pathname?.startsWith("/crm")) return null;

  const [email, setEmail] = React.useState("");
  const [subscribed, setSubscribed] = React.useState(false);
  const [error, setError] = React.useState("");

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!email) {
      setError("Email address is required.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    setSubscribed(true);
    setEmail("");
  };

  const footerLinks = {
    services: [
      { name: "Custom Software", href: "/custom-software-development" },
      { name: "Website Development", href: "/website-development" },
      { name: "Custom CRM Development", href: "/crm-development" },
      { name: "ERP Systems Development", href: "/erp-development" },
      { name: "AI Development & RAG", href: "/ai-development" },
      { name: "AI Automation Workflows", href: "/ai-automation" },
      { name: "Web Applications", href: "/web-application-development" },
      { name: "Mobile Applications", href: "/mobile-app-development" },
      { name: "WhatsApp Automation", href: "/whatsapp-automation" },
      { name: "Software Company Bangalore", href: "/software-development-company-bangalore" },
      { name: "Software Company Mumbai", href: "/software-development-company-mumbai" },
      { name: "Software Company Gurgaon", href: "/software-development-company-gurgaon" },
      { name: "Software Company Dubai", href: "/software-development-company-dubai" },
      { name: "Offshore Development India", href: "/offshore-software-development-india" },
      { name: "B2B Dealer Portals", href: "/b2b-portal-development" },
      { name: "GST Inventory Software", href: "/custom-inventory-software-gst" },
      { name: "Web Development Bangalore", href: "/web-development-company-bangalore" },
      { name: "SaaS Engineering", href: "/saas-development-services" },
      { name: "Cloud & DevOps Systems", href: "/cloud-devops-infrastructure" },
    ],
    industries: [
      { name: "Restaurant Management", href: "/restaurant-management-software" },
      { name: "Warehouse Management (WMS)", href: "/warehouse-management-software" },
      { name: "B2B Food Supply Chain", href: "/b2b-supply-chain-software" },
      { name: "Insurance CRM Software", href: "/insurance-crm-software" },
      { name: "Legal Case Management", href: "/legal-case-management-software" },
      { name: "Staffing Agency Software", href: "/staffing-agency-software" },
      { name: "Pharmacy Software", href: "/pharmacy-software" },
      { name: "Logistics Software", href: "/logistics-software" },
      { name: "Gym Management CRM", href: "/gym-management-software" },
      { name: "Healthcare & Clinic HMIS", href: "/healthcare-software" },
      { name: "Real Estate & Builders CRM", href: "/real-estate-crm" },
      { name: "Telecalling & Call Centers", href: "/telecalling-crm" },
    ],
    company: [
      { name: "Cost Calculator (ROI)", href: "/tools/software-cost-calculator" },
      { name: "Zoho CRM Alternative", href: "/alternatives/zoho-crm-alternative" },
      { name: "Salesforce Alternative", href: "/alternatives/salesforce-alternative" },
      { name: "About Us", href: "/about" },
      { name: "Solutions Map", href: "/solutions" },
      { name: "Pricing & Retainers", href: "/pricing" },
      { name: "CRM Enterprise Portal", href: "/crm" },
      { name: "Case Studies", href: "/case-studies" },
      { name: "Resources Hub", href: "/resources" },
      { name: "Get in Touch", href: "/contact" },
      { name: "Terms & Conditions", href: "/terms" },
      { name: "Privacy (DPDP/GDPR)", href: "/privacy" },
      { name: "Data Processing (DPA)", href: "/dpa" },
      { name: "Service Level (SLA)", href: "/sla" },
      { name: "Refund & Cancellation", href: "/refund" },
      { name: "Acceptable Use (AUP)", href: "/aup" },
    ],
  };

  return (
    <footer className="w-full bg-slate-50 dark:bg-slate-950 border-t border-border-muted/80 pt-16 pb-8 transition-colors duration-300 print:hidden">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 pb-12 border-b border-border-muted/50">
          {/* Brand Info & Newsletter */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            <Link href="/" className="flex items-center gap-3 group">
              <div className="relative h-8 w-8 shrink-0 rounded-xl overflow-hidden flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
                <img src="/brand/app-icon.svg?v=3" alt="Johnson Softwares" className="w-full h-full object-contain" />
              </div>
              <span className="flex flex-col leading-none select-none">
                <span className="font-heading font-black text-sm tracking-[0.14em] uppercase text-foreground leading-tight">
                  JOHNSON
                </span>
                <span className="text-cyan-500 dark:text-cyan-400 tracking-[0.22em] font-heading font-extrabold text-[10px] uppercase group-hover:text-blue-500 transition-colors duration-300 leading-tight">
                  SOFTWARES
                </span>
              </span>
            </Link>
            <p className="text-sm text-text-muted leading-relaxed max-w-sm">
              We design and engineer enterprise-grade scalable software systems, advanced AI automation workflows, and high-performance digital architectures built for business growth.
            </p>

            {/* Newsletter form */}
            <div className="max-w-md">
              <h4 className="font-heading font-semibold text-sm text-foreground mb-2">
                Subscribe to our technology briefings
              </h4>
              {subscribed ? (
                <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/20 text-green-600 dark:text-green-400 rounded-lg text-sm font-medium">
                  <Check className="h-4 w-4" />
                  Subscription confirmed. Thank you!
                </div>
              ) : (
                <form onSubmit={handleSubscribe} className="relative flex gap-2">
                  <input
                    type="email"
                    placeholder="Enter business email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-grow px-4 py-2 text-sm bg-white dark:bg-slate-900 border border-border-muted rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                  <Button type="submit" variant="secondary" className="px-4 py-2 shrink-0">
                    <Send className="h-4 w-4" />
                  </Button>
                </form>
              )}
              {error && <p className="text-xs text-danger mt-1">{error}</p>}
            </div>
          </div>

          {/* Quick Links Maps */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-8 lg:col-span-3">
            <div>
              <h4 className="font-heading font-semibold text-xs text-primary uppercase tracking-widest mb-4">
                Services
              </h4>
              <ul className="flex flex-col gap-2">
                {footerLinks.services.map((link) => (
                  <li key={link.name}>
                    <Link
                      href={link.href}
                      className="text-sm text-text-muted hover:text-primary transition-colors"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-heading font-semibold text-xs text-primary uppercase tracking-widest mb-4">
                Industries
              </h4>
              <ul className="flex flex-col gap-2">
                {footerLinks.industries.map((link) => (
                  <li key={link.name}>
                    <Link
                      href={link.href}
                      className="text-sm text-text-muted hover:text-primary transition-colors"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-heading font-semibold text-xs text-primary uppercase tracking-widest mb-4">
                Company
              </h4>
              <ul className="flex flex-col gap-2">
                {footerLinks.company.map((link) => (
                  <li key={link.name}>
                    <Link
                      href={link.href}
                      className="text-sm text-text-muted hover:text-primary transition-colors"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Technical/Contact Info & Trust Badges */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 py-8 border-b border-border-muted/50 text-sm text-text-muted">
          <div className="flex items-center gap-3">
            <Mail className="h-5 w-5 text-primary shrink-0" />
            <div>
              <span className="block font-semibold text-foreground text-xs">Email us</span>
              <a href="mailto:contact@johnsonsoftwares.com" className="hover:text-primary transition-colors">
                contact@johnsonsoftwares.com
              </a>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Phone className="h-5 w-5 text-primary shrink-0" />
            <div>
              <span className="block font-semibold text-foreground text-xs">Direct call</span>
              <a href="tel:+919880893416" className="hover:text-primary transition-colors">
                +91 98808 93416
              </a>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Clock className="h-5 w-5 text-primary shrink-0" />
            <div>
              <span className="block font-semibold text-foreground text-xs">Operation hours</span>
              <span>Mon - Fri, 9:00 AM - 6:00 PM IST</span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <MapPin className="h-5 w-5 text-primary shrink-0" />
            <div>
              <span className="block font-semibold text-foreground text-xs">Bengaluru Office</span>
              <span>27th Main Rd, HSR Layout, 560102</span>
            </div>
          </div>
        </div>

        {/* Copyright, Socials & Fine print */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-8 text-xs text-text-muted">
          <div className="flex flex-col gap-1 text-center sm:text-left">
            <span>© {new Date().getFullYear()} JOHNSON SOFTWARES. All rights reserved.</span>
            <span>Engineering Digital Excellence. Made for business growth.</span>
          </div>

          {/* Social Links using custom SVGs */}
          <div className="flex items-center gap-4">
            <a
              href="https://linkedin.com"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-primary transition-colors"
            >
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path fillRule="evenodd" d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.779-1.75-1.75s.784-1.75 1.75-1.75 1.75.779 1.75 1.75-.784 1.75-1.75 1.75zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" clipRule="evenodd" />
              </svg>
            </a>
            <a
              href="https://twitter.com"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Twitter/X"
              className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-primary transition-colors"
            >
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </a>
            <a
              href="https://github.com"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GitHub"
              className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-primary transition-colors"
            >
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};
